import { graphQLClient } from '../client';
import { graphql } from '../generated/admin';
import {
  AmazonBusinessAccountInput,
  BrandInput,
  UpdateUserInput,
} from '../generated/admin/graphql';

export const ATTACH_PAYMENT_METHOD = graphql(`
  mutation attachPaymentMethod($input: AttachPaymentMethodInput!) {
    attachPaymentMethod(input: $input) {
      stripeConfig {
        customerId
        paymentMethod {
          ... on PaymentMethodCard {
            last4
            brand
            expMonth
            expYear
          }
          ... on PaymentMethodACH {
            last4AccountNumber
            bank
          }
          ... on PaymentMethodUnknown {
            brand
          }
        }
      }
      pendingMicrodepositVerification {
        hostedVerificationUrl
      }
    }
  }
`);

export const MERCHANT_BY_DOMAIN = graphql(`
  query merchantByDomain($domain: Domain!) {
    merchantByDomain(domain: $domain) {
      id
      integrationStatus
    }
  }
`);

export const UPDATE_BILLING_NOTIFICATIONS = graphql(`
  mutation updateBillingNotifications($input: UpdateBillingNotificationsInput!) {
    updateBillingNotifications(input: $input) {
      settings {
        emails
      }
      error
    }
  }
`);

export const attachPaymentMethod = (id: string, firstName: string, lastName: string) => {
  return {
    input: {
      id,
      billingDetails: {
        firstName,
        lastName,
      },
    },
  };
};

export const SEND_TEST_WEBHOOK = graphql(`
  mutation sendTestWebhook($input: SendTestWebhookInput!) {
    sendTestWebhook(input: $input) {
      id
    }
  }
`);

export const SAVE_WEBHOOK_URL = graphql(`
  mutation saveWebhookURL($input: SaveWebhookURLInput!) {
    saveWebhookURL(input: $input) {
      url
      verified
    }
  }
`);

export const MERCHANTS = graphql(`
  query merchantsByDeveloper {
    merchantsByDeveloper {
      merchants {
        commissionRate
        commissionType
        deliverabilitySupport
        domain
        logo
        name
      }
    }
  }
`);

export const STORES = graphql(`
  query Stores($pagination: PaginationInput, $filters: StoresFiltersInput) {
    stores(pagination: $pagination, filters: $filters) {
      edges {
        node {
          id
          domain
          name
          marketplace
          logoUrl
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`);

export const TOTAL_STORES = graphql(`
  query TotalStores($filters: StoresFiltersInput) {
    totalStores(filters: $filters) {
      count
    }
  }
`);

export const BILLING_ITEMS = graphql(`
  query BillingItems($pagination: BillingItemsPaginationInput, $filters: BillingFiltersInput) {
    billingItems(pagination: $pagination, filters: $filters) {
      edges {
        node {
          createdAt
          id
          orderId
          amount {
            currency
            value
          }
          type
          payer
          receiver
          billingSetting {
            developerCommissionShare
            merchantCommissionRate
            gmvTier {
              min
              max
              percentage
              tier
            }
            volumeTiers {
              volume
              fee
            }
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
        totalPages
      }
    }
  }
`);

export const INVOICES = graphql(`
  query invoices($pagination: InvoicePaginationInput, $filters: InvoiceFiltersInput) {
    invoices(pagination: $pagination, filters: $filters) {
      edges {
        cursor
        node {
          id
          createdAt
          amount {
            value
            currency
          }
          billingPeriod {
            startDate
            endDate
          }
          dueDate
          chargeDate
          number
          status
          items {
            description
            amount {
              value
              currency
            }
            quantity
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`);

export const ADMIN_ORDER_BY_ID = graphql(`
  query OrderById($id: ID!) {
    orderById(id: $id) {
      marketplaceOrderIds
      breakdown {
        subtotalCents
        totalCents
        taxCents
        discountCents
        marginCents
        currency
        shippingCents
        refundCents
      }
      marketplace
      commission {
        rate
        status
      }
      products {
        productId
        quantity
        ... on OrderShopifyProduct {
          variantId
          title
          storeCanonicalURL
        }
      }
    }
  }
`);

export const CREATE_DEVELOPER_TOKEN = graphql(`
  mutation createDeveloperToken($input: CreateDeveloperTokenInput!) {
    createDeveloperToken(input: $input) {
      token
      error
    }
  }
`);

export const DEVELOPERS = graphql(`
  query developers {
    developers {
      developers {
        id
        email
        displayName
        photoURL
      }
      error
    }
  }
`);

export const EXPORT = graphql(`
  mutation exportBillingItems($input: ExportBillingItemsInput!) {
    exportBillingItems(input: $input) {
      success
      error
    }
  }
`);

export const AUTH_AMAZON = graphql(`
  mutation authorizeAmazonBusiness($input: AuthorizeAmazonBusinessInput!) {
    authorizeAmazonBusiness(input: $input) {
      success
      error
    }
  }
`);

export const CHECKOUT_INTENTS = graphql(`
  query checkoutIntents(
    $pagination: PaginationInput
    $filters: CheckoutIntentsFiltersInput
    $sort: CheckoutIntentsSortInput
  ) {
    checkoutIntents(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        node {
          id
          state
          productUrl
          quantity
          failReason
          createdAt
          buyer {
            firstName
            lastName
            email
            address1
            address2
            city
            province
            country
            postalCode
            phone
          }
          offer {
            totalCents
            subtotalCents
            currency
            shippingCents
            taxCents
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`);

export const REACTIVATE_PLAN = graphql(`
  mutation reactivatePlan {
    reactivatePlan {
      success
      error
    }
  }
`);

export const CHECK_AMAZON_AUTH = graphql(`
  query amazonBusinessAuthStatus {
    amazonBusinessAuthStatus {
      status
    }
  }
`);

export const ADMIN_UPDATE_BRAND = graphql(`
  mutation UpdateBrand($input: BrandInput!) {
    updateBrand(input: $input) {
      data {
        user {
          brand {
            companyName
            supportEmail
            logoUrl
            primaryColor
          }
        }
      }
      error
    }
  }
`);

export const ADMIN_UPDATE_USER = graphql(`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      data {
        user {
          firstName
          lastName
          displayName
          apiKey
          adminToken
          authProvider
          email
          id
          webhookURL
          webhookURLVerified
          marginAmount
          marginIsPercent
          photoURL
          isApprovedForDirectCheckout
          website
          inventoryRequestLimits {
            uniqueStoreRequests
            uniqueProductRequests
          }
          jwtValidationKey
          hmacSecretKey
          brand {
            companyName
            supportEmail
            logoUrl
            primaryColor
          }
        }
        amazonBusinessAccount {
          groupId
          buyerEmail
          amazonAccountEmail
          oauthStatus
        }
        billingPeriodSettings {
          gracePeriodDays
          ... on BillingPrePayment {
            currency
            minBalance
            remainingBalance
            lastRefillDate
            startDate
            endDate
          }
          ... on BillingMonthly {
            startDate
            endDate
          }
          ... on BillingSubscription {
            startDate
            endDate
            status
          }
        }
        stripeConfig {
          customerId
          paymentMethod {
            ... on PaymentMethodCard {
              last4
              brand
              expMonth
              expYear
            }
            ... on PaymentMethodACH {
              last4AccountNumber
              bank
            }
            ... on PaymentMethodUnknown {
              brand
            }
          }
        }
        notificationSettings {
          billingNotifications {
            emails
          }
        }
      }
      error
    }
  }
`);

export const ADMIN_UPDATE_AMAZON_BUSINESS_ACCOUNT = graphql(`
  mutation UpdateAmazonBusinessAccount($input: AmazonBusinessAccountInput!) {
    updateAmazonBusinessAccount(input: $input) {
      data {
        user {
          firstName
          lastName
          displayName
          apiKey
          adminToken
          authProvider
          email
          id
          webhookURL
          webhookURLVerified
          marginAmount
          marginIsPercent
          photoURL
          isApprovedForDirectCheckout
          website
          inventoryRequestLimits {
            uniqueStoreRequests
            uniqueProductRequests
          }
          jwtValidationKey
          hmacSecretKey
        }
        amazonBusinessAccount {
          groupId
          buyerEmail
          amazonAccountEmail
          oauthStatus
        }
        billingPeriodSettings {
          ... on BillingPrePayment {
            currency
            minBalance
            gracePeriodDays
            remainingBalance
            lastRefillDate
            startDate
            endDate
          }
          ... on BillingMonthly {
            startDate
            endDate
            gracePeriodDays
          }
        }
        stripeConfig {
          customerId
          paymentMethod {
            ... on PaymentMethodCard {
              last4
              brand
              expMonth
              expYear
            }
            ... on PaymentMethodACH {
              last4AccountNumber
              bank
            }
            ... on PaymentMethodUnknown {
              brand
            }
          }
        }
        notificationSettings {
          billingNotifications {
            emails
          }
        }
      }
      error
    }
  }
`);

export const ADMIN_USER = graphql(`
  query user {
    user {
      data {
        accessScopes
        user {
          firstName
          lastName
          displayName
          apiKey
          apiKeySecondary
          adminToken
          authProvider
          email
          id
          webhookURL
          webhookURLVerified
          marginAmount
          marginIsPercent
          photoURL
          isApprovedForDirectCheckout
          website
          inventoryRequestLimits {
            uniqueStoreRequests
            uniqueProductRequests
          }
          jwtValidationKey
          hmacSecretKey
          brand {
            companyName
            supportEmail
            logoUrl
            primaryColor
          }
        }
        amazonBusinessAccount {
          groupId
          buyerEmail
          amazonAccountEmail
          oauthStatus
        }
        billingPeriodSettings {
          gracePeriodDays
          ... on BillingPrePayment {
            currency
            minBalance
            remainingBalance
            lastRefillDate
            startDate
            endDate
          }
          ... on BillingMonthly {
            startDate
            endDate
          }
          ... on BillingSubscription {
            startDate
            endDate
            status
          }
        }
        stripeConfig {
          customerId
          paymentMethod {
            ... on PaymentMethodCard {
              last4
              brand
              expMonth
              expYear
            }
            ... on PaymentMethodACH {
              last4AccountNumber
              bank
            }
            ... on PaymentMethodUnknown {
              brand
            }
          }
        }
        notificationSettings {
          billingNotifications {
            emails
          }
        }
      }
      error
    }
  }
`);

export const PRODUCTS = graphql(`
  query Products(
    $pagination: CursorPaginationInput
    $filters: ProductsFilterInput
    $sort: ProductsSortInput
  ) {
    products(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        node {
          id
          title
          images {
            url
          }
          url
          price
          currency
          ... on ShopifyProduct {
            vendor
            store
            storeCommissions
            storeLogo
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`);

export const INVENTORY_REQUESTS_DATA = graphql(`
  query inventoryRequestsData {
    inventoryRequests {
      edges {
        cursor
        node {
          id
          data
          requestType
          requestedAt
        }
      }
    }
    storeInventoryRequestsCount
    productInventoryRequestsCount
  }
`);

export const REGENERATE_API_KEY = graphql(`
  mutation regenerateApiKey {
    regenerateApiKey {
      data {
        user {
          apiKey
          apiKeySecondary
        }
      }
      error
    }
  }
`);

export const REVOKE_SECONDARY_API_KEY = graphql(`
  mutation revokeSecondaryApiKey {
    revokeSecondaryApiKey {
      data {
        user {
          apiKey
          apiKeySecondary
        }
      }
      error
    }
  }
`);

export const regenerateApiKey = async () => {
  return graphQLClient.get().mutate({
    mutation: REGENERATE_API_KEY,
    context: { clientName: 'admin' },
  });
};

export const revokeSecondaryApiKey = async () => {
  return graphQLClient.get().mutate({
    mutation: REVOKE_SECONDARY_API_KEY,
    context: { clientName: 'admin' },
  });
};

export const getUser = async (apiKey: string | undefined, userToken: string) => {
  graphQLClient.set(apiKey, userToken);
  return graphQLClient.get().query({
    query: ADMIN_USER,
    context: {
      clientName: 'admin',
    },
  });
};

export const getDevelopers = async (apiKey: string | undefined, userToken: string) => {
  graphQLClient.set(apiKey, userToken);
  return graphQLClient.get().query({
    query: DEVELOPERS,
    context: {
      clientName: 'admin',
    },
  });
};

export const updateAdminUser = async ({
  updates,
  apiKey,
  adminToken,
}: {
  updates: UpdateUserInput;
  apiKey?: string;
  adminToken?: string | null;
}) => {
  graphQLClient.set(apiKey, adminToken);
  return graphQLClient.get().mutate({
    mutation: ADMIN_UPDATE_USER,
    context: {
      clientName: 'admin',
    },
    variables: {
      input: updates,
    },
  });
};

export const updateAdminBrand = async ({
  brand,
  apiKey,
  adminToken,
}: {
  brand: BrandInput;
  apiKey?: string;
  adminToken?: string | null;
}) => {
  graphQLClient.set(apiKey, adminToken);
  return graphQLClient.get().mutate({
    mutation: ADMIN_UPDATE_BRAND,
    context: {
      clientName: 'admin',
    },
    variables: {
      input: brand,
    },
  });
};

export const updateAmazonBusinessAccount = async ({
  updates,
  apiKey,
  adminToken,
}: {
  updates: AmazonBusinessAccountInput;
  apiKey?: string;
  adminToken?: string | null;
}) => {
  graphQLClient.set(apiKey, adminToken);
  return graphQLClient.get().mutate({
    mutation: ADMIN_UPDATE_AMAZON_BUSINESS_ACCOUNT,
    context: {
      clientName: 'admin',
    },
    variables: {
      input: updates,
    },
  });
};

export const createDeveloperToken = async ({
  developerId,
  apiKey,
  userToken,
  readOnly,
}: {
  developerId: string;
  apiKey: string | undefined | null;
  userToken: string | undefined | null;
  readOnly?: boolean;
}) => {
  if (apiKey || userToken) {
    graphQLClient.set(apiKey, userToken);
  }
  return graphQLClient.get().mutate({
    mutation: CREATE_DEVELOPER_TOKEN,
    context: {
      clientName: 'admin',
    },
    variables: {
      input: { developerId, readOnly },
    },
  });
};
